import { GetServerSideProps } from "next";
import Head from "next/head";
import Image from "next/image";
import EventListing from "../../../../components/event-list/EventListing";
import config from "../../../../config";
import {
    DepartmentVisibility,
    EcommerceEvent,
    EcommerceEventList,
    EventListType,
} from "../../../../models/Event";
import { ClientConfiguration } from "../../../../models/ClientConfiguration";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import EventListPasswordGate from "../../../../components/event-list/EventListPasswordGate";
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    IconButton,
    Typography,
} from "@mui/material";
import CartIcon from "@mui/icons-material/ShoppingCart";
import {
    displayDateRange,
    getImageUrl,
    setupStyleMapFromStyleMap,
} from "../../../../utils/helpers";
import React from "react";
import ThemeStore from "../../../../stores/ThemeStore";
import PageWrapper from "../../../../components/PageWrapper";
import DepartmentListing from "../../../../components/event-list/DepartmentListing";
import EventSidebar from "../../../../components/event-list/EventSidebar";
import Link from "next/link";
import Cart from "../../../../components/cart/Cart";
import CartStore, { useCartStore } from "../../../../stores/CartStore";
import SleepIcon from "../../../../icons/SleepIcon";
import NothingHereYet from "../../../../components/NothingHereYet";
import { observer } from "mobx-react-lite";
import AuthStore, { COOKIE_BOXOFFICE_KEY } from "../../../../stores/AuthStore";
import dynamic from "next/dynamic";
import { gtm } from "../../../../utils/gtm";
import { tokenIsValid } from "../../../../utils/jwtHelpers";
import { useRouter } from "next/router";
import CustomEventList from "../../../../components/event-list/CustomEventList";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import { Toaster } from "react-hot-toast";

const DepartmentList = dynamic(
    () => import("../../../../components/event-list/DepartmentList"),
    { ssr: false },
);

const QuickAddBoxOfficeDialog = dynamic(
    () => import("../../../../components/event-list/QuickAddBoxOfficeDialog"),
    { ssr: false },
);

const Calendar = dynamic(
    () => import("../../../../components/calendar/Calendar"),
    { ssr: false },
);

const DinnerEvent = dynamic(
    () => import("../../../../components/dinner/DinnerEvent"),
    { ssr: false },
);

const VipConcertRegistrationFlow = dynamic(
    () => import("../../../../components/concert/VipConcertRegistrationFlow"),
    { ssr: false },
);

const SessionWidget = dynamic(
    () => import("../../../../components/SessionWidget"),
    { ssr: false },
);

const Home = (props: {
    eventList: EcommerceEvent;
    style: EcommerceEventList;
    errorMessage: string | null;
    notPubliclyAvailable?: boolean;
    vipConcertEventList?: EcommerceEventList | null;
    vipConcertClientConfig?: ClientConfiguration | null;
}) => {
    const [showEventDetails, setShowEventDetails] = React.useState(false);
    const [quickAddOpen, setQuickAddOpen] = React.useState(false);

    const cartStore = useCartStore(props.eventList.cartUrl);

    function toggleEventDetails() {
        setShowEventDetails(!showEventDetails);
    }

    React.useEffect(() => {
        if (props.eventList.newStyleMap) {
            setupStyleMapFromStyleMap(props.eventList.newStyleMap);
        }
    }, []);

    const pageTitle = `${
        props.eventList.event?.title ?? props.eventList.clientName
    } Tickets | AuthentiGATE`;

    const isNotPubliclyAvailable = !!props.notPubliclyAvailable;
    const shouldNoIndex =
        isNotPubliclyAvailable ||
        (props.eventList.event !== undefined &&
            props.eventList.event !== null &&
            props.eventList.event.isIndexable === false);

    if (isNotPubliclyAvailable) {
        return (
            <>
                <Head>
                    <title>{pageTitle}</title>
                    <meta name="robots" content="noindex" />
                </Head>
                <PageWrapper eventList={props.style}>
                    <Container maxWidth="md">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <Card sx={{ maxWidth: 600, width: "100%" }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ mb: 3 }}>
                                        This event is not available for online
                                        purchase.
                                    </Typography>
                                    <Link
                                        href={`/store/${props.eventList.cartUrl}`}
                                    >
                                        <Button variant="contained">
                                            Back to Events
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        </Box>
                    </Container>
                </PageWrapper>
            </>
        );
    }

    const eventListType =
        props.eventList.event?.eventList?.type ?? EventListType.DEFAULT;
    const eventListPassword =
        props.eventList.event?.eventList?.config?.password ?? null;

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta
                    name="description"
                    content={`Buy tickets for ${
                        props.eventList.event?.title ??
                        props.eventList.clientName
                    }.`}
                />
                {shouldNoIndex && (
                    <meta name="robots" content="noindex" />
                )}
            </Head>
            <EventListPasswordGate
                password={eventListPassword}
                eventId={props.eventList.event?.id}
            >
                {eventListType === EventListType.DEFAULT && (
                    <PageWrapper
                        eventList={props.style}
                        overrideLogo={
                            props.eventList.event?.eventList?.style.logo
                        }
                    >
                        <Container maxWidth="xl">
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            flexWrap: "wrap",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Link
                                            href={`/store/${props.eventList.cartUrl}`}
                                        >
                                            <Button variant="contained">
                                                Back to Events
                                            </Button>
                                        </Link>
                                        {AuthStore.boxoffice &&
                                            props.eventList.event &&
                                            cartStore && (
                                                <Button
                                                    variant="outlined"
                                                    onClick={() =>
                                                        setQuickAddOpen(true)
                                                    }
                                                >
                                                    Quick Add
                                                </Button>
                                            )}
                                    </Box>
                                </Grid>
                                {props.errorMessage ? (
                                    <Grid item xs={12}>
                                        <NothingHereYet message="Event is not currently active." />
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid
                                            item
                                            xs={12}
                                            md={8}
                                            order={{ xs: 2, md: 1 }}
                                            key={props.eventList.event?.departments
                                                .map((it) => it.id)
                                                .join("")}
                                        >
                                            <DepartmentList
                                                eventList={props.eventList}
                                                cartStore={cartStore}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={4}
                                            order={{ xs: 1, md: 2 }}
                                            key={props.eventList.event?.id}
                                            sx={{
                                                position: { md: "sticky" },
                                                top: "20px",
                                                alignSelf: "flex-start",
                                            }}
                                        >
                                            <EventSidebar
                                                eventList={props.eventList}
                                                showEventDetails={
                                                    showEventDetails
                                                }
                                                onToggleEventDetails={
                                                    toggleEventDetails
                                                }
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            order={3}
                                            key={"checkout-button"}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Link
                                                href={`/store/${props.eventList.cartUrl}/checkout`}
                                            >
                                                <Button variant="contained">
                                                    Proceed to Checkout
                                                </Button>
                                            </Link>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Container>
                    </PageWrapper>
                )}
                {eventListType === EventListType.DATE_SESSION_PICKER && (
                    <PageWrapper
                        eventList={props.style}
                        overrideLogo={
                            props.eventList.event?.eventList?.style?.logo
                        }
                        background={
                            props.eventList.event?.eventList?.style.background
                        }
                    >
                        <Container maxWidth="xl">
                            {props.errorMessage ? (
                                <NothingHereYet message="Event is not currently active." />
                            ) : (
                                <Calendar
                                    style={
                                        props.eventList.event!.eventList!.style
                                    }
                                    config={
                                        props.eventList.event?.eventList
                                            ?.config!
                                    }
                                    departments={
                                        props.eventList.event?.departments ?? []
                                    }
                                    clientIds={
                                        props.eventList.event?.client
                                            .equivalentEMS2ClientIds ?? []
                                    }
                                    eventTitle={props.eventList.event?.title}
                                />
                            )}
                        </Container>
                    </PageWrapper>
                )}
                {eventListType === EventListType.CUSTOM && cartStore && (
                    <PageWrapper
                        eventList={props.style}
                        overrideLogo={
                            props.eventList.event?.eventList?.style.logo
                        }
                        background={
                            props.eventList.event?.eventList?.style.background
                        }
                    >
                        <Container maxWidth="xl">
                            <Link href={`/store/${props.eventList.cartUrl}`}>
                                <Button variant="contained">
                                    Back to Events
                                </Button>
                            </Link>
                            {props.errorMessage ? (
                                <NothingHereYet message="Event is not currently active." />
                            ) : (
                                <CustomEventList
                                    eventList={props.eventList}
                                    cartStore={cartStore}
                                    template={
                                        props.eventList.event!.eventList!.config
                                            .customTemplate!
                                    }
                                />
                            )}
                        </Container>
                    </PageWrapper>
                )}
                {eventListType === EventListType.COMING_SOON && (
                    <PageWrapper
                        eventList={props.style}
                        overrideLogo={
                            props.eventList.event?.eventList?.style.logo
                        }
                        background={
                            props.eventList.event?.eventList?.style.background
                        }
                    >
                        <Container maxWidth="lg">
                            <Grid container spacing={4} justifyContent="center">
                                <Grid item xs={12}>
                                    <Link
                                        href={`/store/${props.eventList.cartUrl}`}
                                    >
                                        <Button variant="contained">
                                            Back to Events
                                        </Button>
                                    </Link>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            textAlign: "center",
                                            py: 6,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor: (theme) =>
                                                    theme.palette.primary.main,
                                                borderRadius: "50%",
                                                width: 120,
                                                height: 120,
                                                mb: 3,
                                                boxShadow: (theme) =>
                                                    `0 10px 30px ${theme.palette.primary.main}30`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <ScheduleIcon
                                                sx={{
                                                    fontSize: 60,
                                                    color: "white",
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="h2"
                                            sx={{
                                                fontWeight: "bold",
                                                mb: 2,
                                                color: (theme) =>
                                                    theme.palette.primary.main,
                                            }}
                                        >
                                            Coming Soon
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            color="text.secondary"
                                            sx={{ mb: 4, maxWidth: 600 }}
                                        >
                                            {props.eventList.event?.title}{" "}
                                            tickets will be available soon. Stay
                                            tuned for updates!
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Container>
                    </PageWrapper>
                )}
                {eventListType === EventListType.VIP_CONCERT &&
                    cartStore &&
                    props.eventList.event &&
                    props.vipConcertEventList && (
                        <Container maxWidth="xl">
                            <Box
                                sx={{
                                    my: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    flexWrap: "wrap",
                                }}
                            >
                                <Link
                                    href={`/store/${props.eventList.cartUrl}`}
                                >
                                    <Button variant="contained">
                                        Back to Events
                                    </Button>
                                </Link>
                                <SessionWidget
                                    clientId={
                                        props.vipConcertEventList.clientId
                                    }
                                />
                            </Box>
                            {props.errorMessage ? (
                                <>
                                    <NothingHereYet message="Event is not currently active." />
                                    <ConfirmDialog />
                                    <Toaster position="top-center" />
                                </>
                            ) : (
                                <GoogleReCaptchaProvider reCaptchaKey="6Lc1XW4kAAAAACMuJ96qI9fgbsm03ItN2ah-fIny">
                                    <VipConcertRegistrationFlow
                                        event={props.eventList.event}
                                        eventList={props.vipConcertEventList}
                                        clientConfig={
                                            props.vipConcertClientConfig ?? null
                                        }
                                    />
                                </GoogleReCaptchaProvider>
                            )}
                        </Container>
                    )}
                {eventListType === EventListType.DINNER_EVENT && (
                    <PageWrapper
                        eventList={props.style}
                        overrideLogo={
                            props.eventList.event?.eventList?.style?.logo
                        }
                        background={
                            props.eventList.event?.eventList?.style.background
                        }
                    >
                        <Container maxWidth="lg">
                            {props.errorMessage ? (
                                <NothingHereYet message="Event is not currently active." />
                            ) : (
                                <DinnerEvent
                                    style={
                                        props.eventList.event!.eventList!.style
                                    }
                                    config={
                                        props.eventList.event?.eventList
                                            ?.config!
                                    }
                                    departments={
                                        props.eventList.event?.departments ?? []
                                    }
                                    clientIds={
                                        props.eventList.event?.client
                                            .equivalentEMS2ClientIds ?? []
                                    }
                                    eventTitle={props.eventList.event?.title}
                                />
                            )}
                        </Container>
                    </PageWrapper>
                )}
            </EventListPasswordGate>
            <Cart />
            {AuthStore.boxoffice && props.eventList.event && cartStore && (
                <QuickAddBoxOfficeDialog
                    open={quickAddOpen}
                    onClose={() => setQuickAddOpen(false)}
                    event={props.eventList.event}
                    cartStore={cartStore}
                />
            )}
        </>
    );
};

export default Home;

export const getServerSideProps: GetServerSideProps = async (context) => {
    const cartUrl = context.params?.cartUrl,
        eventId = context.params?.eventId;
    let data: any,
        style: any,
        error = false,
        errorMessage: string | null = null,
        notPubliclyAvailable = false;

    const boxoffice = context.req.cookies[COOKIE_BOXOFFICE_KEY];
    let salesContext = "ECOMMERCE";

    if (
        boxoffice == "true" &&
        tokenIsValid(
            context.req.cookies[
                config.COOKIE_PREFIX + "__authentigate_token_sanitized"
            ],
        )
    ) {
        salesContext = "BOXOFFICE";
    }

    const startTime = new Date().getTime();

    try {
        const res = await fetch(
            `${config.BACKEND_URI}/sales/ecommerce-events/${cartUrl}/${eventId}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-Sales-Context": salesContext,
                    Cookie: context.req.headers.cookie ?? "",
                },
                credentials: "include",
            },
        );
        if (res.status !== 200) {
            error = true;
            console.log(await res.text());
        } else {
            data = await res.json();
            if (data?.notPubliclyAvailable) {
                notPubliclyAvailable = true;
            } else if (!data.event || !data.event.isActive) {
                errorMessage = "Event is not currently active.";
            }
        }
    } catch (err) {
        console.log(err);
        error = true;
    }

    const endTime = new Date().getTime();
    const timeDiff = endTime - startTime;
    console.log(`Fetch time: ${timeDiff}ms`);

    try {
        const res = await fetch(
            `${config.BACKEND_URI}/sales/cart-style/${cartUrl}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-Sales-Context": "ECOMMERCE",
                },
            },
        );
        if (res.status !== 200) {
            error = true;
            console.log(await res.text());
        } else {
            style = await res.json();
        }
    } catch (err) {
        console.log(err);
        error = true;
    }

    if (data?.newStyleMap) {
        setupStyleMapFromStyleMap(data.newStyleMap);
    }

    let vipConcertEventList: any = null;
    let vipConcertClientConfig: any = null;

    if (data?.event?.eventList?.type === EventListType.VIP_CONCERT) {
        try {
            const res = await fetch(
                `${config.BACKEND_URI}/sales/ecommerce-events/${cartUrl}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-Sales-Context": salesContext,
                        Cookie: context.req.headers.cookie ?? "",
                    },
                    credentials: "include",
                },
            );
            if (res.status === 200) {
                vipConcertEventList = await res.json();
            } else {
                console.log(await res.text());
            }
        } catch (err) {
            console.log(err);
        }

        if (salesContext === "ECOMMERCE") {
            try {
                const res = await fetch(
                    `${config.BACKEND_URI}/sales/client-configuration/${cartUrl}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "X-Sales-Context": "ECOMMERCE",
                        },
                    },
                );
                if (res.status === 200) {
                    vipConcertClientConfig = await res.json();
                } else {
                    console.log(await res.text());
                }
            } catch (err) {
                console.log(err);
            }
        }
    }

    return {
        props: {
            eventList: data,
            style,
            errorMessage,
            notPubliclyAvailable,
            vipConcertEventList,
            vipConcertClientConfig,
        },
        notFound: error,
    };
};
